import { useCallback, useMemo, useState } from 'react';

/**
 * Checked rows in a list, for changing several at once: change a field on
 * any checked row and every checked row takes the new value.
 *
 * Only rows the list is showing count. A row that a filter hides stays
 * remembered but is left out of `selected`, so a change never reaches a
 * row nobody can see — and it is checked again if the filter is cleared.
 */
export function useListSelection<T extends { id: string }>(visible: T[]) {
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());

  const selected = useMemo(
    () => new Set(visible.filter((item) => checked.has(item.id)).map((item) => item.id)),
    [visible, checked]
  );

  const onChange = useCallback(
    (next: ReadonlySet<string>) =>
      setChecked((prev) => {
        // `next` is built from what is shown; keep the hidden ones as they were.
        const shown = new Set(visible.map((item) => item.id));
        const kept = Array.from(prev).filter((id) => !shown.has(id));
        return new Set([...kept, ...Array.from(next)]);
      }),
    [visible]
  );

  const clear = useCallback(() => setChecked(new Set()), []);

  /**
   * The rows a change to `item` applies to: every selected row when `item`
   * is one of them, otherwise just `item` — acting on an unchecked row acts
   * on that row alone. Rows whose `field` is already `value` are left out,
   * so nothing is saved that would not change.
   */
  const targets = useCallback(
    (item: T, field: string, value: string, current: (row: T, field: string) => unknown) =>
      (selected.has(item.id) ? visible.filter((row) => selected.has(row.id)) : [item]).filter(
        (row) => current(row, field) !== value
      ),
    [selected, visible]
  );

  return { selected, onChange, clear, targets };
}

export type ListSelection = Pick<ReturnType<typeof useListSelection>, 'selected' | 'onChange'>;
